import { isDev, VERSION } from '@common'
import * as Sentry from '@sentry/react'
import { logger } from '@utils'

// Get version from package.json or environment variable
// In production, this should be set via build process or environment variable
const APP_VERSION = VERSION || '0.1.0'

const initialize = async () => {
    if (isDev) return

    await Sentry.init({
        dsn: 'https://5d1594d7a9ab785c513b2a9c7a8db454@o419471.ingest.us.sentry.io/4510539438751745',
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 1,
        debug: isDev,
        environment: process.env.REACT_APP_ENV,
        release: APP_VERSION,
    })

    // Set initial version as tag
    Sentry.setTag('version', APP_VERSION)

    logger.info('Sentry initialized')
}

const sendError = (error, label, ...subLabel) => {
    let errorStr = ''
    if (label) errorStr += `[${label}]`
    if (subLabel) subLabel.forEach(sl => (errorStr += `[${sl}]`))
    errorStr += ' ' + error

    logger.error(errorStr)
    if (!isDev) {
        Sentry.captureMessage(errorStr)
    }
}

/**
 * Set member user context in Sentry
 * @param {Object} memberData - Member data object
 * @param {string} memberData.id - Member ID
 * @param {string} memberData.email - Member email
 * @param {string} memberData.name - Member name
 * @param {string} memberData.phone - Member phone
 */
const setMemberContext = memberData => {
    if (isDev) return

    if (!memberData) {
        logger.warn('setMemberContext called with null/undefined memberData')
        return
    }

    try {
        Sentry.setUser({
            id: memberData.id?.toString() || null,
            email: memberData.email || null,
            username: memberData.name || memberData.email || null,
            ip_address: '{{auto}}', // Let Sentry auto-detect IP
        })

        // Set additional context as tags for better filtering
        Sentry.setTag('user_type', 'member')
        if (memberData.id) {
            Sentry.setTag('member_id', memberData.id.toString())
        }
        if (memberData.email) {
            Sentry.setContext('member', {
                id: memberData.id,
                email: memberData.email,
                name: memberData.name,
                phone: memberData.phone,
                status: memberData.status,
            })
        }

        logger.debug('Sentry member context set', { memberId: memberData.id, email: memberData.email })
    } catch (error) {
        logger.error('Error setting Sentry member context:', error)
    }
}

/**
 * Set employee user context in Sentry
 * @param {Object} employeeData - Employee data object
 * @param {string} employeeData.id - Employee ID
 * @param {string} employeeData.email - Employee email
 * @param {string} employeeData.firstName - Employee first name
 * @param {string} employeeData.lastName - Employee last name
 * @param {string} employeeData.role - Employee role
 * @param {string} employeeData.cashRegisterId - Cash register ID
 * @param {string} employeeData.stallId - Stall ID
 */
const setEmployeeContext = employeeData => {
    if (isDev) return

    if (!employeeData) {
        logger.warn('setEmployeeContext called with null/undefined employeeData')
        return
    }

    try {
        const fullName = [employeeData.firstName, employeeData.lastName].filter(Boolean).join(' ') || employeeData.email

        Sentry.setUser({
            id: employeeData.id?.toString() || null,
            email: employeeData.email || null,
            username: fullName,
            ip_address: '{{auto}}', // Let Sentry auto-detect IP
        })

        // Set additional context as tags for better filtering
        Sentry.setTag('user_type', 'employee')
        if (employeeData.id) {
            Sentry.setTag('employee_id', employeeData.id.toString())
        }
        if (employeeData.role) {
            Sentry.setTag('employee_role', employeeData.role)
        }
        if (employeeData.cashRegisterId) {
            Sentry.setTag('cash_register_id', employeeData.cashRegisterId.toString())
        }
        if (employeeData.stallId) {
            Sentry.setTag('stall_id', employeeData.stallId.toString())
        }

        Sentry.setContext('employee', {
            id: employeeData.id,
            email: employeeData.email,
            firstName: employeeData.firstName,
            lastName: employeeData.lastName,
            role: employeeData.role,
            cashRegisterId: employeeData.cashRegisterId,
            stallId: employeeData.stallId,
            cafeId: employeeData.cafeId,
        })

        logger.debug('Sentry employee context set', { employeeId: employeeData.id, email: employeeData.email })
    } catch (error) {
        logger.error('Error setting Sentry employee context:', error)
    }
}

/**
 * Clear user context in Sentry (call on logout)
 */
const clearUserContext = () => {
    if (isDev) return

    try {
        Sentry.setUser(null)
        Sentry.setTag('user_type', null)
        Sentry.setTag('member_id', null)
        Sentry.setTag('employee_id', null)
        Sentry.setTag('employee_role', null)
        Sentry.setTag('cash_register_id', null)
        Sentry.setTag('stall_id', null)
        Sentry.setContext('member', null)
        Sentry.setContext('employee', null)

        logger.debug('Sentry user context cleared')
    } catch (error) {
        logger.error('Error clearing Sentry user context:', error)
    }
}

const utils = {
    initialize,
    sendError,
    setMemberContext,
    setEmployeeContext,
    clearUserContext,
}

export default utils
